import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { MapPin, Phone, Navigation, ExternalLink, Truck, ArrowUp, ArrowDown, Wand2, FileSpreadsheet, FileText } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { API_BASE } from "@/lib/api";

function gmapsLink(address) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
}

function downloadFile(url) {
  const token = localStorage.getItem("token");
  fetch(url, {
    credentials: "include",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
    .then(async (r) => {
      if (!r.ok) throw new Error(await r.text());
      const blob = await r.blob();
      const disp = r.headers.get("Content-Disposition") || "";
      const match = disp.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : "reporte";
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 60000);
    })
    .catch(() => toast.error("No se pudo descargar el reporte"));
}

export default function MisEntregas() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [coords, setCoords] = useState(null);
  const [loading, setLoading] = useState(false);
  const [optBusy, setOptBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/piloto/mis-entregas");
      setOrders(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, []);

  const persistOrder = async (list) => {
    try {
      await api.patch("/piloto/ruta", { order_ids: list.map((o) => o.id) });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const move = (idx, delta) => {
    const next = [...orders];
    const target = idx + delta;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setOrders(next);
    persistOrder(next);
  };

  const shareLocation = () => {
    if (!navigator.geolocation) return toast.error("Tu navegador no soporta geolocalización");
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        setCoords({ lat: latitude, lng: longitude });
        try {
          await api.post("/piloto/ubicacion", { lat: latitude, lng: longitude });
          toast.success("Ubicación compartida");
        } catch (e) { toast.error(formatApiError(e)); }
        setLoading(false);
      },
      (err) => {
        setLoading(false);
        toast.error(`No se pudo obtener ubicación: ${err.message}`);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const optimizarRuta = async () => {
    if (!coords) {
      toast.error("Comparte tu ubicación primero");
      return;
    }
    setOptBusy(true);
    try {
      const { data } = await api.post("/piloto/ruta/optimizar");
      toast.success(`Ruta optimizada · ${data.geocoded} direcciones geocodificadas${data.not_geocoded ? ` · ${data.not_geocoded} no encontradas` : ""}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setOptBusy(false);
    }
  };

  const setEstado = async (id, valor) => {
    try {
      await api.patch(`/orders/${id}/subestado`, { campo: "estado_entrega", valor });
      toast.success("Estado actualizado");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="mis-entregas-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-semibold">Panel del piloto</div>
          <h1 className="font-manrope font-black tracking-tight text-4xl mt-1 flex items-center gap-3">
            <Truck className="w-8 h-8 text-[#3B82F6]" /> Mis Entregas
          </h1>
          <p className="text-zinc-500 mt-2 text-sm">Hola {user?.name?.split(" ")[0]}, aquí están tus pedidos activos de ciudad.</p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button onClick={shareLocation} disabled={loading} variant="outline" className="gap-2 border-zinc-300" data-testid="share-location-btn">
            <Navigation className="w-4 h-4" /> {loading ? "Obteniendo..." : "Compartir ubicación"}
          </Button>
          <Button onClick={optimizarRuta} disabled={optBusy || !coords} className="bg-[#002FA7] hover:bg-[#00227A] gap-2" data-testid="optimize-route-btn">
            <Wand2 className="w-4 h-4" /> {optBusy ? "Optimizando..." : "Optimizar ruta"}
          </Button>
          <Button onClick={() => downloadFile(`${API_BASE}/reports/hoja-piloto.xlsx`)} variant="outline" className="gap-2 border-emerald-200 text-emerald-800 hover:bg-emerald-50" data-testid="hp-me-xlsx">
            <FileSpreadsheet className="w-4 h-4" /> Mi hoja Excel
          </Button>
          <Button onClick={() => downloadFile(`${API_BASE}/reports/hoja-piloto.pdf`)} variant="outline" className="gap-2 border-red-200 text-red-800 hover:bg-red-50" data-testid="hp-me-pdf">
            <FileText className="w-4 h-4" /> Mi hoja PDF
          </Button>
        </div>
      </header>

      {coords && (
        <Card className="p-4 border-emerald-200 bg-emerald-50 text-emerald-900 rounded-md text-sm">
          <div className="font-semibold">Ubicación compartida</div>
          <div className="text-xs mt-0.5">Lat: {coords.lat.toFixed(6)} · Lng: {coords.lng.toFixed(6)}</div>
        </Card>
      )}

      <div className="text-xs text-zinc-500">
        Usa las flechas para reordenar manualmente, o presiona <span className="font-semibold">Optimizar ruta</span> para calcular el orden más cercano desde tu ubicación (OpenStreetMap).
      </div>

      <div className="space-y-3">
        {orders.length === 0 && (
          <div className="text-center text-zinc-400 text-sm py-16 border border-dashed rounded-md">
            No tienes entregas activas asignadas.
          </div>
        )}
        {orders.map((o, idx) => (
          <Card key={o.id} className="border-zinc-200 rounded-md hover:shadow-sm transition-transform duration-200 ease-out">
            <div className="flex">
              {/* Left rail — sequence + move */}
              <div className="w-14 shrink-0 bg-zinc-900 text-white flex flex-col items-center justify-between p-3 rounded-l-md">
                <div className="text-center">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-400">Parada</div>
                  <div className="font-manrope font-black text-2xl leading-none mt-1">{idx + 1}</div>
                </div>
                <div className="flex flex-col gap-1">
                  <button onClick={() => move(idx, -1)} disabled={idx === 0} className="p-1 rounded hover:bg-zinc-800 disabled:opacity-30" data-testid={`up-${o.numero}`} title="Subir">
                    <ArrowUp className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => move(idx, 1)} disabled={idx === orders.length - 1} className="p-1 rounded hover:bg-zinc-800 disabled:opacity-30" data-testid={`down-${o.numero}`} title="Bajar">
                    <ArrowDown className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              {/* Body */}
              <div className="flex-1 p-4 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">{o.numero}</div>
                    <div className="font-semibold mt-0.5 truncate">{o.cliente_nombre}</div>
                    {o.envio_zona && <div className="text-[11px] text-zinc-500">Zona: {o.envio_zona}</div>}
                  </div>
                  <div className="text-right shrink-0">
                    <StatusBadge status={o.estado_entrega} />
                    <div className="mt-1 text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">A cobrar</div>
                    <div className="font-manrope font-black text-lg text-emerald-700 leading-none">
                      Q {Number(o.a_cobrar != null ? o.a_cobrar : (o.total - (o.anticipo || 0))).toFixed(2)}
                    </div>
                    {o.anticipo ? <div className="text-[10px] text-zinc-500 mt-0.5">Anticipo Q {Number(o.anticipo).toFixed(2)}</div> : null}
                  </div>
                </div>

                <div className="mt-3 text-xs space-y-1">
                  {o.cliente_telefono && (
                    <a href={`tel:${o.cliente_telefono}`} className="inline-flex items-center gap-2 text-blue-700 hover:underline mr-4">
                      <Phone className="w-3 h-3" /> {o.cliente_telefono}
                    </a>
                  )}
                  {o.cliente_telefono2 && (
                    <a href={`tel:${o.cliente_telefono2}`} className="inline-flex items-center gap-2 text-blue-500 hover:underline mr-4">
                      <Phone className="w-3 h-3" /> alt {o.cliente_telefono2}
                    </a>
                  )}
                  <div className="flex items-start gap-2 text-zinc-700"><MapPin className="w-3 h-3 mt-0.5 shrink-0" /><span>{o.direccion}</span></div>
                  <a
                    href={gmapsLink(o.direccion)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[#002FA7] font-semibold uppercase tracking-widest text-[10px] mt-1"
                    data-testid={`open-map-${o.numero}`}
                  >
                    Abrir en Google Maps <ExternalLink className="w-3 h-3" />
                  </a>
                </div>

                <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
                  <Button size="sm" variant="outline" className="bg-blue-50 border-blue-200 text-blue-900" onClick={() => setEstado(o.id, "en_ruta")} data-testid={`me-camino-${o.numero}`}>En ruta</Button>
                  <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => setEstado(o.id, "entregado")} data-testid={`me-entregado-${o.numero}`}>Entregado</Button>
                  <Button size="sm" variant="outline" className="text-red-600" onClick={() => setEstado(o.id, "fallido")}>Fallido</Button>
                  <Link to={`/pedidos/${o.id}`} className="inline-flex items-center justify-center rounded-md border border-zinc-200 text-xs font-semibold uppercase tracking-widest hover:bg-zinc-50 gap-1">
                    Ver <ExternalLink className="w-3 h-3" />
                  </Link>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
